import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AuthContext } from './../AuthContext';
import Sidebar from './../Sidebar';
import Footer from './../Footer';
import { site_url } from "./../config.js";
import axios from 'axios';
import { Link } from "react-router-dom";

function Customer() {
  const { user } = React.useContext(AuthContext);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [shopCategories, setShopCategories] = useState([]);
  const [selectedShopCategory, setSelectedShopCategory] = useState('');
  const [loadingCategories, setLoadingCategories] = useState(false);
  const observer = useRef();
  const lastCustomerElementRef = useRef();

  const ITEMS_PER_PAGE = 20;

  // Fetch shop categories from API
  const fetchShopCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await axios.get(site_url + '/shop_cates');
      
      if (response.data && response.data.category) {
        setShopCategories(response.data.category);
      }
    } catch (error) {
      setLoadingCategories(false);

      console.error('Error fetching shop categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch customers from API
  const fetchCustomers = async (pageNum, search = '', shopCategory = '') => {
    if (loading) return;
    
    setLoading(true);
    try {
      const requestData = {
        page: pageNum,
        limit: ITEMS_PER_PAGE,
        search: search
      };
      
      // Add shop category filter if selected
      if (shopCategory) {
        requestData.shop_category = shopCategory;
      }
      
      const response = await axios.post(site_url + '/customer_list', requestData);

      if (response.data.status === 'success') {
        const newCustomers = response.data.customers || [];
        
        if (pageNum === 1) {
          setCustomers(newCustomers);
        } else {
          setCustomers(prev => [...prev, ...newCustomers]);
        }
        
        setHasMore(newCustomers.length === ITEMS_PER_PAGE);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Search customers
  const searchCustomers = async (searchTerm) => {
    if (searchTerm.trim() === '') {
      setFilteredCustomers([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setLoading(true);
    
    try {
      const requestData = {
        page: 1,
        limit: 100, // Get more results for search
        search: searchTerm
      };
      
      // Add shop category filter if selected
      if (selectedShopCategory) {
        requestData.shop_category = selectedShopCategory;
      }
      
      const response = await axios.post(site_url + '/customer_list', requestData);

      if (response.data.status === 'success') {
        setFilteredCustomers(response.data.customers || []);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.trim() === '') {
      setFilteredCustomers([]);
      setIsSearching(false);
    } else {
      // Debounce search
      const timeoutId = setTimeout(() => {
        searchCustomers(value);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  };

  // Handle shop category selection
  const handleShopCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedShopCategory(categoryId);
    
    // Reset pagination and fetch customers with new filter
    setPage(1);
    setHasMore(true);
    fetchCustomers(1, searchTerm, categoryId);
  };

  // Load more customers
  const loadMore = () => {
    if (!loading && hasMore && !isSearching) {
      fetchCustomers(page + 1, searchTerm, selectedShopCategory);
    }
  };

  // Intersection Observer for infinite scroll
  const lastCustomerElementRefCallback = (node) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !isSearching) {
        loadMore();
      }
    });
    
    if (node) observer.current.observe(node);
  };

  // Initial load
  useEffect(() => {
    fetchCustomers(1);
  }, []);

  useEffect(() => {
    fetchShopCategories();
  }, []);

  useEffect(() => {
    fetchCustomers(1, searchTerm, selectedShopCategory);
  }, [selectedShopCategory]);
  // Get customers to display
  const displayCustomers = isSearching ? filteredCustomers : customers;

  return (
    <>
      <div id="appCapsule">
        {/* Header */}
        <div className="section mt-2">
          <div className="section-heading">
            <h2>Customer List</h2>
          </div>
        </div>

        {/* Search Box and Shop Category Filter */}
        <div className="section">
          <div className="form-group">
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
              </div>
              <input
                type="text"
                className="form-control"
                placeholder="Search by username, shop name, phone..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          {/* Shop Category Filter */}
          <div className="form-group">
            <label className="form-label">Route</label>
            <select
              className="form-control"
              value={selectedShopCategory}
              onChange={handleShopCategoryChange}
              disabled={loadingCategories}
            >
              <option value="">All Categories</option>
              {shopCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name} ({category.ename})
                </option>
              ))}
            </select>
            {loadingCategories && (
              <small className="text-muted">
                <i className="fas fa-spinner fa-spin"></i> Loading categories...
              </small>
            )}
          </div>
        </div>

        {/* Customer List */}
        <div className="section">
          {loading && customers.length === 0 && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          )}

          {displayCustomers.length === 0 && !loading && (
            <div className="text-center py-4">
              <p className="text-muted">No customers found</p>
            </div>
          )}

          <div className="transactions">
            {displayCustomers.map((customer, index) => (
              <div
                key={customer.id || index}
                ref={index === displayCustomers.length - 1 ? lastCustomerElementRefCallback : null}
                className="item"
              >
                <div className="detail">
                  <div className="image-block imaged w48">
                    {customer.shop_img ? (
                      <img 
                        src={customer.shop_img} 
                        alt="Shop" 
                        style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover' }}
                      />
                    ) : (
                      <i className="fas fa-user-circle text-primary" style={{ fontSize: '48px' }}></i>
                    )}
                  </div>
                  <div>
                    <strong>{customer.username || customer.name || 'N/A'}</strong>
                    <p className="text-muted mb-1">
                      {customer.shop_name_tamil || customer.shop_name || 'No shop name'}
                    </p>
                    <p className="text-muted mb-0">
                      <i className="fas fa-phone"></i> 
                      {customer.phone ? (
                        <a href={`tel:${customer.phone}`} className="text-decoration-none">
                          {customer.phone}
                        </a>
                      ) : (
                        'N/A'
                      )}
                    </p>
                  </div>
                </div>
                <div className="right">
                  <Link 
                    to={`/customer/edit/${customer.id}`} 
                    className="btn btn-sm btn-outline-primary me-2"
                    title="Edit Customer"
                  >
                    <i className="fas fa-edit"></i>
                  </Link>
                  
                </div>
              </div>
            ))}
          </div>

          {/* Loading indicator for pagination */}
          {loading && customers.length > 0 && (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="sr-only">Loading more...</span>
              </div>
              <p className="text-muted mt-2">Loading more customers...</p>
            </div>
          )}

          {/* End of list indicator */}
          {!hasMore && customers.length > 0 && !isSearching && (
            <div className="text-center py-3">
              <p className="text-muted">No more customers to load</p>
            </div>
          )}
        </div>
      </div>

      <Footer />
      <Sidebar />
    </>
  );
}

export default Customer;
